import React, { useEffect, useRef, useState } from "react";
import "./ECommerceDetails.css";
import { submitProductEnquiry } from '../../api';
import toast, { Toaster } from "react-hot-toast";

/* =====================================================
   IMAGES (E-Commerce & Tech Focused)
===================================================== */

const images = {
  hero: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1400&q=90",
  inventory: "https://images.unsplash.com/photo-1586528116311-ad8ed79abdfa?auto=format&fit=crop&w=1000&q=90",
  dashboard: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1000&q=90",
  integration1: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&w=900&q=85",
  integration2: "https://images.unsplash.com/photo-1587614382346-4ec70e388b28?auto=format&fit=crop&w=900&q=85",
  integration3: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=85",
  integration4: "https://images.unsplash.com/photo-1556740738-b6a63e27c4df?auto=format&fit=crop&w=900&q=85",
  hosting: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1400&q=90",
  security: "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=1400&q=90",
  delivery: "https://images.unsplash.com/photo-1580674285054-bed31e145f59?auto=format&fit=crop&w=1400&q=90",
  mobile: "https://images.unsplash.com/photo-1512428559087-560fa5ceab42?auto=format&fit=crop&w=1400&q=90",
  storefront: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1400&q=90",
  client1: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=500&q=85",
  client2: "https://images.unsplash.com/photo-1556742044-3c52d6e88c62?auto=format&fit=crop&w=500&q=85",
  client3: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=500&q=85",
  event: "https://images.unsplash.com/photo-1556761175-5973dc0f32d7?auto=format&fit=crop&w=1000&q=85",
};

/* =====================================================
   PLATFORM STATS
===================================================== */

const platformStats = [
  ["10K+", "Active Stores"],
  ["$5B+", "GMV Processed"],
  ["99.9%", "Server Uptime"],
  ["24/7", "Premium Support"],
];

/* =====================================================
   MANAGEMENT FEATURES
===================================================== */

const managementFeatures = [
  { number: "01", icon: "◈", title: "Inventory Management", text: "Track stock levels in real-time, get low-stock alerts, and manage product variants across multiple warehouses effortlessly." },
  { number: "02", icon: "↗", title: "Order Fulfillment", text: "Automate order processing, print shipping labels, and send tracking updates to your customers automatically." },
  { number: "03", icon: "!", title: "Secure Payments", text: "Accept credit cards, UPI, wallets, and global payments securely with integrated payment gateways and fraud protection." },
  { number: "04", icon: "#", title: "Analytics & Reports", text: "Make data-driven decisions with detailed reports on sales, customer behavior, best-selling products, and revenue." },
  { number: "05", icon: "◇", title: "Marketing & CRM", text: "Run email campaigns, recover abandoned carts, and manage customer relationships from a single unified dashboard." },
];

/* =====================================================
   OMNICHANNEL SOLUTIONS
===================================================== */

const academicPrograms = [
  { title: "Online Storefront", grade: "WEB E-COMMERCE", text: "Build a beautiful, high-converting online store with customizable themes, fast loading speeds, and SEO optimization." },
  { title: "Mobile App Builder", grade: "M-COMMERCE", text: "Convert your store into a native iOS & Android app. Increase customer loyalty with push notifications and easy checkout." },
  { title: "Point of Sale (POS)", grade: "RETAIL STORES", text: "Unify your online and physical retail stores. Sync inventory, orders, and customer data across all your locations." },
  { title: "Social Selling", grade: "SOCIAL COMMERCE", text: "Sell directly on Instagram, Facebook, and WhatsApp. Turn your social media followers into loyal customers instantly." },
];

/* =====================================================
   INTEGRATIONS
===================================================== */

const faculty = [
  { name: "Payment Gateways", role: "Stripe, Razorpay, PayPal", image: images.integration1, text: "Accept global and local payments seamlessly with zero hidden fees and PCI-DSS compliant secure checkout." },
  { name: "Shipping Partners", role: "FedEx, BlueDart, Delhivery", image: images.integration2, text: "Automate your logistics. Generate airway bills, calculate dynamic shipping rates, and track orders in real-time." },
  { name: "Marketing Tools", role: "Mailchimp, Klaviyo, Meta", image: images.integration3, text: "Sync your customer data to powerful marketing tools to run retargeting ads and automated email flows." },
  { name: "Accounting & ERP", role: "QuickBooks, Tally, Zoho", image: images.integration4, text: "Keep your books accurate. Automatically sync sales, taxes, and refunds with your favorite accounting software." },
];

/* =====================================================
   INFRASTRUCTURE
===================================================== */

const campusBlocks = [
  { title: "Cloud Infrastructure", image: images.hosting, text: "Hosted on enterprise-grade AWS servers ensuring your store remains incredibly fast even during massive traffic spikes." },
  { title: "Bank-Grade Security", image: images.security, text: "Free SSL certificates, DDoS protection, and automated daily backups keep your business data and customers safe." },
  { title: "Global CDN", image: images.storefront, text: "Deliver a blazing fast shopping experience worldwide with our Global Content Delivery Network optimizing images and code." },
  { title: "Headless Commerce API", image: images.mobile, text: "Decouple your frontend from the backend. Use our robust APIs to build custom shopping experiences on any device or screen." },
  { title: "Automated Logistics", image: images.delivery, text: "Connect with over 50+ logistics partners globally. Manage returns, calculate shipping costs, and streamline your supply chain." },
];

/* =====================================================
   GROWTH TOOLS
===================================================== */

const studentLife = [
  { number: "01", icon: "✦", title: "Abandoned Cart Recovery", text: "Automatically send personalized emails and SMS to customers who left items in their cart, boosting revenue by up to 20%." },
  { number: "02", icon: "↗", title: "SEO Optimization", text: "Built-in SEO tools help you rank higher on Google. Auto-generated sitemaps, meta tags, and optimized URLs." },
  { number: "03", icon: "◌", title: "Flash Sales & Discounts", text: "Easily schedule discount codes, BOGO offers, and percentage-based sales during holiday seasons or clearance events." },
  { number: "04", icon: "♡", title: "Loyalty Programs", text: "Reward your best customers with points, store credit, and VIP tiers to increase repeat purchases and lifetime value." },
];

/* =====================================================
   EVENTS & WEBINARS
===================================================== */

const schoolEvents = [
  { date: "12", month: "SEP", title: "Black Friday Mastery", category: "WEBINAR", text: "Learn how to scale your servers, optimize checkout, and run profitable ads for the upcoming holiday season." },
  { date: "18", month: "SEP", title: "New POS System Launch", category: "PRODUCT UPDATE", text: "Join our product team as we unveil the new offline Point of Sale hardware integration for retail stores." },
  { date: "25", month: "SEP", title: "D2C Founders Meetup", category: "COMMUNITY", text: "Network with top Direct-to-Consumer brand founders, share growth strategies, and learn industry secrets." },
];

/* =====================================================
   STORE SETUP STEPS
===================================================== */

const admissionSteps = [
  { number: "01", title: "Create Account", text: "Sign up in seconds. Choose your business name, set up your profile, and access your centralized admin dashboard." },
  { number: "02", title: "Design Your Store", text: "Pick a premium template. Use our drag-and-drop builder to customize colors, fonts, and layout without any coding." },
  { number: "03", title: "Upload Products", text: "Add your products, set pricing, upload HD images, and manage variants. Bulk import via CSV is also supported." },
  { number: "04", title: "Launch & Sell", text: "Connect your custom domain, enable your preferred payment gateway, and start receiving orders from globally." },
];

/* =====================================================
   TESTIMONIALS
===================================================== */

const testimonials = [
  { quote: "Switching to Yashvitech+ was the best decision for our clothing brand. Our site speed doubled, and managing inventory is now effortless.", name: "Rahul Verma", role: "Founder, Urban Threads", image: images.client1 },
  { quote: "The built-in marketing tools and abandoned cart recovery increased our monthly revenue by 25%. Highly recommended for scaling D2C brands.", name: "Sneha Kapoor", role: "CEO, Glow Beauty Cosmetics", image: images.client2 },
  { quote: "Managing our physical retail stores and online website from one dashboard saved us countless hours of manual data entry.", name: "Vikram Das", role: "Director, ElectroWorld", image: images.client3 },
];

/* =====================================================
   ACHIEVEMENTS
===================================================== */

const achievements = [
  ["01", "High Conversion Rates", "Our checkout flow is optimized for speed and trust, resulting in 30% higher conversions than industry average."],
  ["02", "Omnichannel Ready", "Sell seamlessly across Web, Mobile, Instagram, Facebook, and physical retail stores simultaneously."],
  ["03", "Zero Transaction Fees", "Unlike other platforms, we don't take a cut of your revenue. Keep 100% of what you earn using our integrated gateway."],
  ["04", "Enterprise Scalability", "From your first sale to your millionth order, our robust cloud infrastructure scales automatically with your business."],
];

/* =====================================================
   CONTACT INTERESTS
===================================================== */

const interests = [
  "Start an Online Store",
  "Migrate from Shopify/WooCommerce",
  "Point of Sale (POS)",
  "Enterprise Solutions",
  "Partner/Agency Program",
];

/* =====================================================
   HOME COMPONENT
===================================================== */

function EcommerceLanding() {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [headerGlow, setHeaderGlow] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    interested: [],
    message: "",
  });

  const facultyRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setHeaderGlow(window.scrollY > 30);
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const elements = document.querySelectorAll(".erp-reveal");
    if (!elements.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("erp-reveal-show");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: "0px 0px -30px 0px" }
    );
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (!element) return;
    setMobileMenu(false);
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollFaculty = (direction) => {
    if (!facultyRef.current) return;
    const amount = window.innerWidth <= 700 ? window.innerWidth * 0.84 : 390;
    facultyRef.current.scrollBy({ left: direction === "right" ? amount : -amount, behavior: "smooth" });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInterest = (interest) => {
    setFormData((prev) => {
      const exists = prev.interested.includes(interest);
      return { ...prev, interested: exists ? prev.interested.filter((item) => item !== interest) : [...prev.interested, interest] };
    });
  };

  // ✅ CHANGED — Toast with name + validation
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation — email ya phone mein se ek zaroori
    if (!formData.email.trim() && !formData.mobile.trim()) {
      toast.error("Please enter either Email or Phone number.");
      return;
    }

    setLoading(true);

    try {
      const result = await submitProductEnquiry({
        name: formData.name,
        email: formData.email || "",
        phone: formData.mobile,
        company: formData.mobile,
        product: 'Ecommerce',
        message: formData.message,
      });

      if (result.status === 'success') {
        toast.success(
          `Congratulations ${formData.name}! Your form is submitted successfully. We will respond to you as soon as possible.`,
          { duration: 5000 }
        );
        setFormData({ name: '', mobile: '', email: '', interested: [], message: '' });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error('Unable to save contact:', error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="erp-single-page">
      {/* ✅ Toast Container */}
      <Toaster
        position="top-right"
        containerStyle={{ top: 80, zIndex: 99999 }}
        toastOptions={{ duration: 5000, style: { fontSize: "13px", borderRadius: "10px", maxWidth: "380px" } }}
      />

      {/* NAVBAR */}
      <nav className={`erp-navbar ${headerGlow ? "erp-navbar-scrolled" : ""}`}>
        <div className="erp-navbar-logo">
          <div className="erp-logo-icon">◈</div>
          <div>Yashvitech<span>+</span><small>E-COMMERCE PLATFORM</small></div>
        </div>

        <div className={`erp-navbar-links ${mobileMenu ? "erp-mobile-open" : ""}`}>
          <button onClick={() => scrollToSection("home")}>Home</button>
          <button onClick={() => scrollToSection("about")}>Platform</button>
          <button onClick={() => scrollToSection("features")}>Features</button>
          <button onClick={() => scrollToSection("dashboard")}>Dashboard</button>
          <button onClick={() => scrollToSection("contact")}>Contact</button>
        </div>

        <button className="erp-navbar-button" onClick={() => scrollToSection("admissions")}>Start Free Trial <span>↗</span></button>
        <button className="erp-mobile-menu-button" onClick={() => setMobileMenu((prev) => !prev)}>
          <span></span><span></span><span></span>
        </button>
      </nav>

      {/* HERO SECTION */}
      <section id="home" className="erp-home-section">
        <div className="erp-home-left erp-reveal">
          <div className="erp-home-badge"><span className="erp-badge-dot"></span>ECOMMERCE SOLUTION</div>
          <h1>Manage Your<br /><span>Business.</span><br />With Confidence.</h1>
          <p className="erp-home-text">A modern e-commerce platform designed to help D2C brands build, manage, and scale their online and offline retail stores from one centralized dashboard.</p>

          <div className="erp-home-actions">
            <button className="erp-home-main-btn" onClick={() => scrollToSection("dashboard")}>View Store Dashboard <span>↗</span></button>
            <button className="erp-home-outline-btn" onClick={() => scrollToSection("about")}>Discover Features</button>
          </div>

          <div className="erp-home-trust">
            <div className="erp-trust-avatars"><div>D2C</div><div>B2B</div><div>POS</div><div>+</div></div>
            <div className="erp-trust-content"><strong>Trusted by 10,000+ Brands</strong><span>Built for global retail scaling</span></div>
          </div>
        </div>

        <div className="erp-home-right erp-reveal">
          <div className="erp-hero-glow"></div>
          <div className="erp-hero-image-box">
            <img src={images.hero} alt="Ecommerce Dashboard" />
            <div className="erp-image-overlay"></div>

            <div className="erp-hero-top-card">
              <div className="erp-top-card-icon">✓</div>
              <div><span>Server Status</span><strong>All Systems Healthy</strong></div>
              <div className="erp-online-dot"></div>
            </div>

            <div className="erp-hero-stock-card">
              <span>Total Revenue</span>
              <div className="erp-stock-number">$42.8K</div>
              <div className="erp-stock-growth"><span>↗ 18.2%</span><small>vs last month</small></div>
            </div>

            <div className="erp-hero-bottom-card">
              <div className="erp-bottom-title"><span>Sales Overview</span><small>Today</small></div>
              <div className="erp-stock-bars">
                <div className="erp-bar erp-bar-one"></div><div className="erp-bar erp-bar-two"></div>
                <div className="erp-bar erp-bar-three"></div><div className="erp-bar erp-bar-four"></div>
                <div className="erp-bar erp-bar-five"></div><div className="erp-bar erp-bar-six"></div>
                <div className="erp-bar erp-bar-seven"></div>
              </div>
              <div className="erp-stock-labels"><span>8</span><span>9</span><span>10</span><span>11</span><span>12</span><span>1</span><span>2</span></div>
            </div>

            <div className="erp-products-added-card">
              <div className="erp-product-mini-icon">🛒</div>
              <div className="erp-product-mini-content"><span>Orders Today</span><strong>426 Processed</strong></div>
            </div>
          </div>
        </div>

        <div className="erp-home-bottom-stats">
          <div className="erp-stat-item"><strong>10K+</strong><span>Active Stores</span></div>
          <div className="erp-stat-line"></div>
          <div className="erp-stat-item"><strong>$5B+</strong><span>Sales Processed</span></div>
          <div className="erp-stat-line"></div>
          <div className="erp-stat-item"><strong>50+</strong><span>Integrations</span></div>
          <div className="erp-stat-line"></div>
          <div className="erp-stat-item"><strong>24/7</strong><span>Expert Support</span></div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="erp-school-stat-strip erp-reveal">
        {platformStats.map(([number, label]) => (
          <div className="erp-school-stat" key={label}><strong>{number}</strong><span>{label}</span></div>
        ))}
      </section>

      {/* ABOUT / PLATFORM */}
      <section id="about" className="erp-about-section">
        <div className="erp-about-image-area erp-reveal">
          <div className="erp-about-main-image"><img src={images.dashboard} alt="Analytics" loading="lazy" /></div>
          <div className="erp-about-small-image"><img src={images.inventory} alt="Warehouse" loading="lazy" /></div>
          <div className="erp-about-experience-card"><strong>$0</strong><div><span>Setup</span><small>Transaction Fees</small></div></div>
          <div className="erp-about-circle"><span>SCALE</span><strong>FASTER</strong></div>
        </div>

        <div className="erp-about-content erp-reveal">
          <div className="erp-about-tag"><span></span>ABOUT Yashvitech+</div>
          <h2>Commerce Meets<br /><span>Smart Automation.</span></h2>
          <p className="erp-about-intro">Our e-commerce management platform brings inventory, orders, marketing, and customer data together in one centralized environment.</p>
          <p className="erp-about-description">Instead of using different tools for your website, POS, and inventory, manage your entire retail empire through a structured digital system.</p>

          <div className="erp-about-features">
            <div className="erp-about-feature"><div className="erp-feature-icon">◈</div><div><h3>Centralized Dashboard</h3><p>Keep inventory, orders, and customer data organized in one place.</p></div></div>
            <div className="erp-about-feature"><div className="erp-feature-icon">◇</div><div><h3>Omnichannel Selling</h3><p>Sell on your website, social media, and physical retail stores simultaneously.</p></div></div>
            <div className="erp-about-feature"><div className="erp-feature-icon">↗</div><div><h3>Faster Growth</h3><p>Use built-in marketing and analytics to scale your revenue faster.</p></div></div>
          </div>

          <button className="erp-about-button" onClick={() => scrollToSection("features")}>Explore Features <span>↗</span></button>
        </div>

        <div className="erp-about-bottom">
          <div><strong>01</strong><span>Inventory Sync</span></div>
          <div><strong>02</strong><span>Order Tracking</span></div>
          <div><strong>03</strong><span>Payments</span></div>
          <div><strong>04</strong><span>Sales Reports</span></div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="erp-features-section">
        <div className="erp-features-header erp-reveal">
          <div className="erp-features-heading">
            <div className="erp-features-tag"><span></span>POWERFUL FEATURES</div>
            <h2>Everything Your<br />Store Needs To<span> Scale Faster.</span></h2>
          </div>
          <p className="erp-features-intro">From inventory control and automated fulfillment to secure checkouts and marketing, our platform handles the heavy lifting.</p>
        </div>

        <div className="erp-features-grid">
          {managementFeatures.map((feature) => (
            <div className={`erp-feature-card ${feature.number === "01" ? "erp-feature-large" : ""} ${feature.number === "05" ? "erp-feature-wide" : ""} erp-reveal`} key={feature.number}>
              <div className="erp-feature-card-top">
                <div className="erp-feature-number">{feature.number}</div>
                <div className="erp-feature-card-icon">{feature.icon}</div>
              </div>

              <div className="erp-feature-card-content">
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </div>

              {feature.number === "01" && (
                <div className="erp-inventory-mini-ui">
                  <div className="erp-mini-ui-header"><span>Stock Overview</span><small>Live Sync</small></div>
                  <div className="erp-mini-stock-row"><span>In Stock</span><div className="erp-mini-progress"><div className="erp-progress-one"></div></div><strong>85%</strong></div>
                  <div className="erp-mini-stock-row"><span>Low Stock</span><div className="erp-mini-progress"><div className="erp-progress-two" style={{ width: '12%', background: '#f4c95d' }}></div></div><strong style={{ color: '#f4c95d' }}>12%</strong></div>
                  <div className="erp-mini-stock-row"><span>Out of Stock</span><div className="erp-mini-progress"><div className="erp-progress-three" style={{ width: '3%', background: '#ef6b6b' }}></div></div><strong style={{ color: '#ef6b6b' }}>3%</strong></div>
                </div>
              )}

              {feature.number === "02" && (
                <div className="erp-insight-chart">
                  <div className="erp-chart-line erp-line-one"></div><div className="erp-chart-line erp-line-two"></div>
                  <div className="erp-chart-line erp-line-three"></div><div className="erp-chart-line erp-line-four"></div>
                  <div className="erp-chart-line erp-line-five"></div>
                </div>
              )}

              {feature.number === "03" && (
                <div className="erp-alert-ui">
                  <div className="erp-alert-item">
                    <div className="erp-alert-dot"></div>
                    <div><strong>Payout Received</strong><span>Transfer to bank successful</span></div>
                    <small>SUCCESS</small>
                  </div>
                  <div className="erp-alert-item">
                    <div className="erp-alert-dot" style={{ background: '#ef6b6b' }}></div>
                    <div><strong>Fraud Alert</strong><span>Suspicious transaction blocked</span></div>
                    <small style={{ color: '#ef6b6b' }}>BLOCKED</small>
                  </div>
                </div>
              )}

              {feature.number === "04" && (
                <div className="erp-category-ui">
                  <div><span>Total Revenue</span><strong>$142,500</strong></div>
                  <div><span>Conversion Rate</span><strong>4.2%</strong></div>
                  <div><span>Average Order Value</span><strong>$85.50</strong></div>
                </div>
              )}

              {feature.number === "05" && (
                <div className="erp-control-ui">
                  <div className="erp-control-circle"><span>STORE</span><strong>98%</strong><small>Healthy</small></div>
                  <div className="erp-control-stats">
                    <div><strong>12.5K</strong><span>Subscribers</span></div>
                    <div><strong>$15K</strong><span>Cart Recovery</span></div>
                    <div><strong>65%</strong><span>Repeat Buyers</span></div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="erp-features-bottom">
          <div><strong>01</strong><span>Manage Inventory</span></div>
          <div><strong>02</strong><span>Process Orders</span></div>
          <div><strong>03</strong><span>Accept Payments</span></div>
          <div><strong>04</strong><span>Grow Sales</span></div>
        </div>
      </section>

      {/* DASHBOARD */}
      <section id="dashboard" className="erp-inventory-section">
        <div className="erp-inventory-header erp-reveal">
          <div>
            <div className="erp-inventory-tag"><span></span>ADMIN DASHBOARD</div>
            <h2>Your Store.<br /><span>Your Control.</span></h2>
          </div>
          <p>Monitor sales, fulfill orders, manage customers, and track your revenue growth from one clean and organized admin panel.</p>
        </div>

        <div className="erp-inventory-dashboard erp-reveal">
          <div className="erp-dashboard-top">
            <div><span>OVERVIEW</span><h3>Store Management Dashboard</h3></div>
            <div className="erp-dashboard-date"><span>●</span>Live Sales Data</div>
          </div>

          <div className="erp-inventory-stats">
            <div className="erp-inventory-stat-card"><div className="erp-stat-icon">◈</div><div><span>Total Revenue</span><strong>$42,850</strong></div><small>+18.6%</small></div>
            <div className="erp-inventory-stat-card"><div className="erp-stat-icon">◇</div><div><span>Total Orders</span><strong>845</strong></div><small>+24 new</small></div>
            <div className="erp-inventory-stat-card"><div className="erp-stat-icon">!</div><div><span>Conversion Rate</span><strong>4.2%</strong></div><small className="erp-warning-text">Today</small></div>
            <div className="erp-inventory-stat-card"><div className="erp-stat-icon">↗</div><div><span>Active Visitors</span><strong>142</strong></div><small>Live</small></div>
          </div>

          <div className="erp-inventory-main-grid">
            <div className="erp-products-panel">
              <div className="erp-panel-heading">
                <div><span>ORDERS</span><h3>Recent Orders</h3></div>
                <button>View All →</button>
              </div>

              <div className="erp-product-table">
                <div className="erp-product-table-head"><span>Product / Customer</span><span>Amount</span><span>Payment</span><span>Status</span></div>

                <div className="erp-product-row">
                  <div className="erp-product-info">
                    <div className="erp-student-mini-image"><img src={images.integration1} alt="Product" /></div>
                    <div><strong>Wireless Noise Cancelling Pros</strong><span>John Doe • #ORD-1024</span></div>
                  </div>
                  <span className="erp-product-category">$299.00</span>
                  <strong className="erp-product-stock">Paid</strong>
                  <span className="erp-status erp-available">Processing</span>
                </div>

                <div className="erp-product-row">
                  <div className="erp-product-info">
                    <div className="erp-student-mini-image"><img src={images.integration2} alt="Product" /></div>
                    <div><strong>Smart Fitness Watch Series 5</strong><span>Sarah Smith • #ORD-1098</span></div>
                  </div>
                  <span className="erp-product-category">$199.00</span>
                  <strong className="erp-product-stock">Paid</strong>
                  <span className="erp-status erp-available" style={{ background: 'rgba(255, 138, 61, 0.1)', color: '#ff8a3d' }}>Shipped</span>
                </div>

                <div className="erp-product-row">
                  <div className="erp-product-info">
                    <div className="erp-student-mini-image"><img src={images.integration3} alt="Product" /></div>
                    <div><strong>Minimalist Desk Lamp</strong><span>Mike Johnson • #ORD-1132</span></div>
                  </div>
                  <span className="erp-product-category">$89.00</span>
                  <strong className="erp-product-stock" style={{ color: '#ef6b6b' }}>Failed</strong>
                  <span className="erp-status erp-available" style={{ background: 'rgba(239, 107, 107, 0.1)', color: '#ef6b6b' }}>Canceled</span>
                </div>
              </div>
            </div>

            <div className="erp-inventory-side-panel">
              <div className="erp-panel-heading"><div><span>SALES GOAL</span><h3>Monthly Target</h3></div></div>
              <div className="erp-attendance-ring"><div className="erp-attendance-ring-inner"><strong>85%</strong><span>Achieved</span></div></div>
              <div className="erp-attendance-list">
                <div><span>Target Revenue</span><strong>$50,000</strong></div>
                <div><span>Current Revenue</span><strong>$42,850</strong></div>
                <div><span>Remaining</span><strong>$7,150</strong></div>
              </div>
            </div>

            <div className="erp-dashboard-health">
              <div className="erp-panel-heading">
                <div><span>STORE HEALTH</span><h3>Performance metrics</h3></div>
                <span className="erp-healthy-badge">Optimized</span>
              </div>
              <div className="erp-health-grid">
                <div><span>Page Speed</span><strong>98/100</strong><div className="erp-health-bar"><i></i></div></div>
                <div><span>Checkout Success</span><strong>94%</strong><div className="erp-health-bar"><i></i></div></div>
                <div><span>Server Uptime</span><strong>99.9%</strong><div className="erp-health-bar"><i></i></div></div>
                <div><span>SEO Score</span><strong>92%</strong><div className="erp-health-bar"><i></i></div></div>
              </div>
            </div>

            <div className="erp-quick-actions">
              <div className="erp-panel-heading"><div><span>MANAGEMENT</span><h3>Quick Actions</h3></div></div>
              <div className="erp-quick-action-grid">
                <button><span>+</span>Add Product</button>
                <button><span>✓</span>Fulfill Orders</button>
                <button><span>%</span>Create Discount</button>
                <button><span>↗</span>Send Campaign</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OMNICHANNEL PROGRAMS */}
      <section className="erp-education-section">
        <div className="erp-education-header erp-reveal">
          <div>
            <div className="erp-education-tag"><span></span>OMNICHANNEL COMMERCE</div>
            <h2>Sell Everywhere<br /><span>Your Customers Are.</span></h2>
          </div>
          <p>Don't limit your business to just one channel. Manage your website, mobile app, social media, and retail stores from a single backend.</p>
        </div>

        <div className="erp-education-grid">
          {academicPrograms.map((program, index) => (
            <article className="erp-education-card erp-reveal" key={program.title}>
              <div className="erp-education-card-top"><span>0{index + 1}</span><span>↗</span></div>
              <small>{program.grade}</small>
              <h3>{program.title}</h3>
              <p>{program.text}</p>
              <div className="erp-education-line"></div>
              <span className="erp-education-link">Explore Channel →</span>
            </article>
          ))}
        </div>
      </section>

      {/* INTEGRATIONS */}
      <section className="erp-faculty-section">
        <div className="erp-faculty-header erp-reveal">
          <div>
            <div className="erp-faculty-tag"><span></span>ECOSYSTEM INTEGRATIONS</div>
            <h2>Connect Your<br /><span>Favorite Tools.</span></h2>
          </div>
          <div className="erp-faculty-controls">
            <button onClick={() => scrollFaculty("left")}>←</button>
            <button onClick={() => scrollFaculty("right")}>→</button>
          </div>
        </div>

        <div className="erp-faculty-list" ref={facultyRef}>
          {faculty.map((person, index) => (
            <article className="erp-faculty-card erp-reveal" key={person.name}>
              <div className="erp-faculty-image">
                <img src={person.image} alt={person.name} loading="lazy" />
                <div className="erp-faculty-number">0{index + 1}</div>
              </div>
              <div className="erp-faculty-content">
                <span>{person.role}</span>
                <h3>{person.name}</h3>
                <p>{person.text}</p>
                <div className="erp-faculty-bottom"><span>Verified Integration</span><b>↗</b></div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* INFRASTRUCTURE */}
      <section id="campus" className="erp-campus-section">
        <div className="erp-campus-header erp-reveal">
          <div>
            <div className="erp-campus-tag"><span></span>INFRASTRUCTURE</div>
            <h2>Technology Designed<br />For <span>Enterprise Scale.</span></h2>
          </div>
          <p>Our architecture combines ultra-fast cloud hosting, military-grade security, global CDNs, and robust APIs to ensure your store never goes down.</p>
        </div>

        <div className="erp-campus-list">
          {campusBlocks.map((block, index) => (
            <article className="erp-campus-card erp-reveal" key={block.title}>
              <div className="erp-campus-image">
                <img src={block.image} alt={block.title} loading="lazy" />
                <div className="erp-campus-number">0{index + 1}</div>
              </div>
              <div className="erp-campus-content">
                <div className="erp-campus-line"><span>→</span></div>
                <p className="erp-eyebrow">TECH STACK / 0{index + 1}</p>
                <h3>{block.title}</h3>
                <p>{block.text}</p>
                <button onClick={() => scrollToSection("contact")}>Learn More <span>↗</span></button>
              </div>
            </article>
          ))}
        </div>

        <div className="erp-campus-stats erp-reveal">
          <div className="erp-campus-stat-card"><strong>99.9%</strong><h3>Uptime</h3><p>Guaranteed server reliability</p></div>
          <div className="erp-campus-stat-card"><strong>Tier 1</strong><h3>Security</h3><p>PCI-DSS Level 1 Compliant</p></div>
          <div className="erp-campus-stat-card"><strong>150+</strong><h3>CDN Nodes</h3><p>Lightning fast global loading</p></div>
          <div className="erp-campus-stat-card"><strong>24/7</strong><h3>Monitoring</h3><p>Continuous threat detection</p></div>
        </div>
      </section>

      {/* GROWTH TOOLS */}
      <section className="erp-student-life-section">
        <div className="erp-student-life-image erp-reveal">
          <img src={images.event} alt="Growth and Sales" loading="lazy" />
          <div className="erp-student-life-card"><strong>40%</strong><span>Average Boost</span><small>In Revenue within 6 Months</small></div>
        </div>

        <div className="erp-student-life-content erp-reveal">
          <div className="erp-student-life-tag"><span></span>GROWTH TOOLS</div>
          <h2>Selling Doesn't<br />Stop At The<span> Checkout.</span></h2>
          <p>Grow your brand with built-in marketing features that recover lost sales, increase average order value, and turn one-time buyers into lifelong loyal customers.</p>

          <div className="erp-student-life-list">
            {studentLife.map((item) => (
              <div className="erp-student-life-item" key={item.number}>
                <div className="erp-life-number">{item.number}</div>
                <div className="erp-life-icon">{item.icon}</div>
                <div><h3>{item.title}</h3><p>{item.text}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EVENTS & WEBINARS */}
      <section id="events" className="erp-events-section">
        <div className="erp-events-header erp-reveal">
          <div>
            <div className="erp-events-tag"><span></span>COMMUNITY & LEARNING</div>
            <h2>Webinars, Events<br />& <span>Product Updates.</span></h2>
          </div>
          <p>Stay ahead of the curve. Join our e-commerce experts for live training sessions, feature announcements, and community networking.</p>
        </div>

        <div className="erp-events-grid">
          <div className="erp-events-feature erp-reveal">
            <div className="erp-events-feature-image"><img src={images.dashboard} alt="Event" loading="lazy" /></div>
            <div className="erp-events-feature-content">
              <span>FEATURED WEBINAR</span>
              <h3>Mastering E-Commerce<br /><strong>Holiday Sales 2026.</strong></h3>
              <p>An exclusive deep dive into inventory planning, Facebook ads scaling, and checkout optimization techniques to dominate Q4 sales.</p>
              <button onClick={() => scrollToSection("contact")}>Register Free <span>↗</span></button>
            </div>
          </div>

          <div className="erp-events-list">
            {schoolEvents.map((event) => (
              <article className="erp-event-item erp-reveal" key={event.title}>
                <div className="erp-event-date"><strong>{event.date}</strong><span>{event.month}</span></div>
                <div className="erp-event-content">
                  <span>{event.category}</span>
                  <h3>{event.title}</h3>
                  <p>{event.text}</p>
                </div>
                <button onClick={() => scrollToSection("contact")}>↗</button>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* LAUNCH STEPS */}
      <section id="admissions" className="erp-admissions erp-section">
        <div className="erp-admissions-heading erp-reveal">
          <div>
            <p className="erp-eyebrow">LAUNCH YOUR STORE</p>
            <h2>Start Selling<br /><span>In Minutes.</span></h2>
          </div>
          <p className="erp-section-intro">Building your online empire shouldn't be complicated. Our seamless onboarding process guides you from account creation to your first successful sale.</p>
        </div>

        <div className="erp-admission-steps">
          {admissionSteps.map((step) => (
            <article className="erp-admission-step erp-reveal" key={step.number}>
              <div className="erp-admission-number">{step.number}</div>
              <div><h3>{step.title}</h3><p>{step.text}</p></div>
              <span className="erp-admission-arrow">↗</span>
            </article>
          ))}
        </div>

        <div className="erp-admission-cta erp-reveal">
          <div>
            <span>14-DAY FREE TRIAL</span>
            <h3>Ready to build your dream store?</h3>
            <p>No credit card required. Cancel anytime. Expert support included.</p>
          </div>
          <button type="button" onClick={() => scrollToSection("contact")}>Start Free Trial <span>→</span></button>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="erp-testimonials erp-section">
        <div className="erp-testimonials-heading erp-reveal">
          <div>
            <p className="erp-eyebrow">MERCHANT SUCCESS</p>
            <h2>What D2C<br /><span>Founders Say.</span></h2>
          </div>
          <div className="erp-testimonials-heading-right">
            <p className="erp-section-intro">We power thousands of fast-growing brands globally. Hear how Yashvitech+ transformed their retail operations.</p>
            <div className="erp-community-rating">
              <div className="erp-rating-number">4.9</div>
              <div><div className="erp-stars">★★★★★</div><span>Average Merchant Rating</span></div>
            </div>
          </div>
        </div>

        <div className="erp-testimonial-feature erp-reveal">
          <div className="erp-testimonial-feature-image">
            <img src={images.hero} alt="Happy merchant" loading="lazy" />
            <div className="erp-image-overlay-card">
              <div className="erp-overlay-icon">✓</div>
              <div><strong>Trusted by Top Brands</strong><span>Scaling global D2C commerce</span></div>
            </div>
          </div>

          <div className="erp-testimonial-feature-content">
            <p className="erp-feature-label">WHY BRANDS CHOOSE US</p>
            <h3>More Than Software,<br /><span>A Growth Partner.</span></h3>
            <p className="erp-feature-description">We provide the enterprise-grade infrastructure and marketing tools you need so you can focus entirely on building great products and a loyal community.</p>

            <div className="erp-feature-points">
              <div className="erp-feature-point">
                <div className="erp-point-number">01</div>
                <div><strong>Zero Technical Headaches</strong><span>No servers to manage, no code to write. We handle it all.</span></div>
              </div>
              <div className="erp-feature-point">
                <div className="erp-point-number">02</div>
                <div><strong>Lightning Fast Checkout</strong><span>Optimized flows that reduce cart abandonment dramatically.</span></div>
              </div>
              <div className="erp-feature-point">
                <div className="erp-point-number">03</div>
                <div><strong>Complete Inventory Sync</strong><span>Never oversell across your physical and online channels again.</span></div>
              </div>
            </div>
          </div>
        </div>

        <div className="erp-testimonial-grid">
          {testimonials.map((item, index) => (
            <article className="erp-testimonial-card erp-reveal" key={item.name}>
              <div className="erp-testimonial-card-top">
                <div className="erp-testimonial-avatar"><img src={item.image} alt={item.name} loading="lazy" /></div>
                <div className="erp-testimonial-person"><strong>{item.name}</strong><span>{item.role}</span></div>
                <div className="erp-testimonial-number">0{index + 1}</div>
              </div>
              <div className="erp-testimonial-rating"><span>★★★★★</span><small>VERIFIED MERCHANT</small></div>
              <div className="erp-quote-mark">“</div>
              <p>{item.quote}</p>
              <div className="erp-testimonial-footer"><span>Verified Business</span><span className="erp-verified-icon">✓</span></div>
            </article>
          ))}
        </div>

        <div className="erp-testimonial-stats">
          <div className="erp-testimonial-stat"><strong>10,000+</strong><span>Active Merchants</span></div>
          <div className="erp-testimonial-stat"><strong>$5B+</strong><span>Total Sales Generated</span></div>
          <div className="erp-testimonial-stat"><strong>120+</strong><span>Countries Supported</span></div>
          <div className="erp-testimonial-stat"><strong>4.9/5</strong><span>Support Rating</span></div>
        </div>
      </section>

      {/* ACHIEVEMENTS */}
      <section id="achievements" className="erp-rank erp-section">
        <div className="erp-rank-heading erp-reveal">
          <p className="erp-eyebrow">PLATFORM ADVANTAGES</p>
          <h2>Built For<br /><span>High Performance.</span></h2>
        </div>
        <div className="erp-rank-list erp-reveal">
          {achievements.map((rank) => (
            <div className="erp-rank-item" key={rank[0]}>
              <span>{rank[0]}</span><h3>{rank[1]}</h3><p>{rank[2]}</p><b>↗</b>
            </div>
          ))}
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="erp-contact-section">
        <div className="erp-contact-heading erp-reveal">
          <div>
            <div className="erp-contact-tag"><span></span>CONTACT SALES</div>
            <h2>Let's Grow Your<br /><span>Business Together.</span></h2>
          </div>
          <p>Whether you are starting a new brand or migrating from an existing platform like Shopify or WooCommerce, our enterprise team is here to help.</p>
        </div>

        <div className="erp-contact-grid">
          <div className="erp-contact-info erp-reveal">
            <div className="erp-contact-info-card">
              <span>GLOBAL HEADQUARTERS</span>
              <h3>Yashvitech+ Commerce Inc.</h3>
              <p>A leading e-commerce SaaS platform helping D2C brands, B2B wholesalers, and retail businesses scale globally.</p>
            </div>
            <div className="erp-contact-detail"><span>SALES EMAIL</span><strong>sales@Yashvitechplus.com</strong></div>
            <div className="erp-contact-detail"><span>PARTNERSHIPS</span><strong>partners@Yashvitechplus.com</strong></div>
            <div className="erp-contact-detail"><span>SUPPORT PHONE</span><strong>+1 (800) 123-4567</strong></div>

            <div className="erp-contact-map-card">
              <div><span>HELP CENTER</span><strong>Visit Knowledge Base</strong></div>
              <button onClick={() => window.scrollTo(0, 0)}>↗</button>
            </div>
          </div>

          <form className="erp-contact-form erp-reveal" onSubmit={handleSubmit}>
            <div className="erp-form-row">
              <div className="erp-form-group">
                <label>FULL NAME</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="John Doe" required />
              </div>
              <div className="erp-form-group">
                <label>STORE URL / BUSINESS NAME</label>
                <input type="text" name="mobile" value={formData.mobile} onChange={handleChange} placeholder="www.mybrand.com" />
              </div>
            </div>

            <div className="erp-form-group">
              <label>WORK EMAIL ADDRESS (optional)</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="john@mybrand.com" />
            </div>

            <div className="erp-form-group">
              <label>I AM INTERESTED IN</label>
              <div className="erp-interest-list">
                {interests.map((interest) => (
                  <button type="button" key={interest} className={formData.interested.includes(interest) ? "erp-interest erp-active" : "erp-interest"} onClick={() => handleInterest(interest)}>{interest}</button>
                ))}
              </div>
            </div>

            <div className="erp-form-group">
              <label>TELL US ABOUT YOUR REQUIREMENTS</label>
              <textarea name="message" value={formData.message} onChange={handleChange} placeholder="Current monthly revenue, migration needs, custom API access..." rows="4"></textarea>
            </div>

            <button type="submit" className="erp-contact-submit" disabled={loading}>
              {loading ? "Sending..." : "Request Demo & Pricing"} <span>↗</span>
            </button>

            <small style={{ display: "block", textAlign: "center", fontSize: "11px", color: "#94a3b8", marginTop: "8px" }}>
              Enter at least one — Email or Phone — so we can reach you back.
            </small>
          </form>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="erp-footer">
        <div className="erp-footer-top">
          <div className="erp-footer-brand">
            <div className="erp-footer-logo">
              <div className="erp-logo-icon">◈</div>
              <div>Yashvitech<span>+</span><small>E-COMMERCE PLATFORM</small></div>
            </div>
            <p>The all-in-one commerce platform to start, run, and grow a business. Sell online, on social media, or in person effortlessly.</p>
            <button className="erp-footer-button" onClick={() => scrollToSection("contact")}>Start 14-Day Free Trial <span>↗</span></button>
          </div>

          <div className="erp-footer-column">
            <h3>PLATFORM</h3>
            <button>Online Store Builder</button>
            <button>Point of Sale (POS)</button>
            <button>B2B Wholesale</button>
            <button>Headless Commerce</button>
            <button>Pricing & Plans</button>
          </div>

          <div className="erp-footer-column">
            <h3>SOLUTIONS</h3>
            <button>Sell on Instagram/TikTok</button>
            <button>Inventory Management</button>
            <button>International Selling</button>
            <button>Marketing Automations</button>
            <button>Analytics & Reporting</button>
          </div>

          <div className="erp-footer-column erp-footer-contact">
            <h3>CONTACT SALES</h3>
            <div><span>EMAIL</span><strong>sales@Yashvitechplus.com</strong></div>
            <div><span>PHONE</span><strong>+1 (800) 123-4567</strong></div>
            <div><span>PARTNERS</span><strong>Join Agency Partner Program</strong></div>
          </div>
        </div>

        <div className="erp-footer-bottom">
          <p>© 2026 <strong>Yashvitech+ Commerce Inc.</strong> All rights reserved.</p>
          <div className="erp-footer-bottom-links">
            <button>Privacy Policy</button>
            <button>Terms of Service</button>
            <button>Sitemap</button>
          </div>
          <p className="erp-footer-made">Built for <span>global retail scaling.</span></p>
        </div>
      </footer>
    </div>
  );
}

export default EcommerceLanding;